import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User } from '../../types';
import { authService } from '../../services/auth';

const initialState: AuthState = {
  token: localStorage.getItem('token'),
  user: null,
  loading: false,
  error: null,
};

export const loginUser = createAsyncThunk('auth/login', async (credentials: { email: string; password: string }, { rejectWithValue }) => {
  try {
    const res = await authService.login(credentials);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Login failed');
  }
});

export const registerUser = createAsyncThunk('auth/register', async (data: any, { rejectWithValue }) => {
  try {
    const res = await authService.register(data);
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.response?.data?.errors || 'Registration failed');
  }
});

export const fetchMe = createAsyncThunk('auth/me', async (_, { rejectWithValue }) => {
  try {
    const res = await authService.me();
    return res.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch user');
  }
});

export const logoutUser = createAsyncThunk('auth/logout', async () => {
  try { await authService.logout(); } catch {}
  localStorage.removeItem('token');
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => { state.user = action.payload; },
    clearError: (state) => { state.error = null; },
    updateWallets: (state, action: PayloadAction<any>) => {
      if (state.user) {
        state.user.real_wallet = action.payload.real_wallet;
        state.user.demo_wallet = action.payload.demo_wallet;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(loginUser.fulfilled, (s, a) => {
        s.loading = false;
        s.token = a.payload.access_token;
        s.user = a.payload.user;
        localStorage.setItem('token', a.payload.access_token);
      })
      .addCase(loginUser.rejected, (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(registerUser.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(registerUser.fulfilled, (s, a) => {
        s.loading = false;
        s.token = a.payload.access_token;
        s.user = a.payload.user;
        localStorage.setItem('token', a.payload.access_token);
      })
      .addCase(registerUser.rejected, (s, a) => { s.loading = false; s.error = a.payload as string; })
      .addCase(fetchMe.fulfilled, (s, a) => { s.user = a.payload.user; })
      .addCase(logoutUser.fulfilled, (s) => { s.token = null; s.user = null; });
  },
});

export const { setUser, clearError, updateWallets } = authSlice.actions;
export default authSlice.reducer;
